import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import phdImage from '../assets/phd_research.png';
import iotImage from '../assets/iot_devices.png';
import techImage from '../assets/tech_domains.png';

const processes = [
  {
    step: '01',
    title: 'Share Requirements',
    desc: 'Tell us your project topic, course, university guidelines and deadline. Our team analyses your needs immediately.',
    icon: (
      <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"/>
      </svg>
    ),
  },
  {
    step: '02',
    title: 'Expert Assignment',
    desc: 'We match you with a domain expert who holds advanced degrees in your exact technology stack and research area.',
    icon: (
      <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"/>
      </svg>
    ),
  },
  {
    step: '03',
    title: 'Development & Research',
    desc: 'Your project is developed with full implementation, documentation, results analysis and plagiarism-free research content.',
    icon: (
      <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5"/>
      </svg>
    ),
  },
  {
    step: '04',
    title: 'Review & Delivery',
    desc: 'Review the complete deliverables. We provide unlimited revisions and viva support until you are 100% satisfied.',
    icon: (
      <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
      </svg>
    ),
  },
];

const testimonials = [
  {
    name: 'Priya Sharma',
    course: 'MTech CSE, IIT Delhi',
    text: 'Connect Students helped me build a computer vision project that got published in IEEE. Their AI expertise is genuinely world-class. My professor was amazed by the quality of implementation!',
    rating: 5,
    avatar: 'PS',
    color: 'bg-indigo-600',
  },
  {
    name: 'Rahul Verma',
    course: 'BTech ECE, NIT Warangal',
    text: 'Got my IoT-based smart agriculture project done in 2 weeks. The documentation, circuit diagrams and code were all perfect. Scored A+ in my viva. Highly recommend!',
    rating: 5,
    avatar: 'RV',
    color: 'bg-emerald-600',
  },
  {
    name: 'Dr. Anjali Patel',
    course: 'PhD Scholar, IISc Bangalore',
    text: 'Their PhD research support is exceptional. They helped me structure my dissertation, run statistical analysis, and even assisted with journal paper submission. Truly a royal service!',
    rating: 5,
    avatar: 'AP',
    color: 'bg-rose-600',
  },
  {
    name: 'Karthik Nair',
    course: 'MBA, XLRI Jamshedpur',
    text: 'The data analytics dashboard they built for my capstone project was phenomenal. Industry-grade Tableau dashboards with complete analysis. Impressed my entire panel!',
    rating: 5,
    avatar: 'KN',
    color: 'bg-amber-600',
  },
  {
    name: 'Sneha Reddy',
    course: 'MCA, Osmania University',
    text: 'Amazing web development project — full MERN stack application with AI recommendations. The team responded at midnight to fix a bug before my presentation. Incredible dedication!',
    rating: 5,
    avatar: 'SR',
    color: 'bg-violet-600',
  },
  {
    name: 'Arjun Mehta',
    course: 'BSc IT, Mumbai University',
    text: 'Even for my diploma project, they treated me with the same premium service. Professional code, neat documentation and they even prepared my PPT. Worth every rupee!',
    rating: 5,
    avatar: 'AM',
    color: 'bg-cyan-600',
  },
];

export default function ProcessTestimonials() {
  const { ref: processRef, inView: processInView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const { ref: testimonialsRef, inView: testimonialsInView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const { ref: galleryRef, inView: galleryInView } = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <>
      {/* Process Section */}
      <section className="section-pad bg-parchment relative overflow-hidden">
        <div className="absolute top-0 right-0 w-80 h-80 rounded-full bg-royal-gold/5 translate-x-1/2 -translate-y-1/2 blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div ref={processRef} className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={processInView ? { opacity: 1, y: 0 } : {}}
              className="gold-badge mb-6"
            >
              How It Works
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={processInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.1 }}
              className="section-title mb-4"
            >
              Simple. Fast.{' '}
              <span className="text-gradient-gold">Guaranteed.</span>
            </motion.h2>
            <motion.div
              initial={{ scaleX: 0 }}
              animate={processInView ? { scaleX: 1 } : {}}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="royal-divider w-24 mx-auto mt-6"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
            {/* Connector line */}
            <div className="hidden lg:block absolute top-16 left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-transparent via-royal-gold/40 to-transparent" />

            {processes.map((process, i) => (
              <motion.div
                key={process.step}
                initial={{ opacity: 0, y: 40 }}
                animate={processInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: i * 0.15, duration: 0.6 }}
                className="relative flex flex-col items-center text-center"
                id={`process-step-${process.step}`}
              >
                {/* Step circle */}
                <div className="relative mb-6">
                  <div className="w-16 h-16 rounded-full bg-royal-navy border-4 border-royal-gold flex items-center justify-center text-white shadow-gold animate-pulse-gold">
                    {process.icon}
                  </div>
                  <div className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-royal-gold text-royal-navy font-playfair font-bold text-xs flex items-center justify-center shadow-sm">
                    {process.step}
                  </div>
                </div>

                <h3 className="font-playfair font-bold text-royal-navy text-xl mb-3">{process.title}</h3>
                <p className="font-inter text-sm text-slate-royal/70 leading-relaxed">{process.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery / Image Showcase */}
      <section className="section-pad bg-ivory" ref={galleryRef}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={galleryInView ? { opacity: 1, y: 0 } : {}}
              className="section-title mb-4"
            >
              Research That Defines{' '}
              <span className="text-gradient-gold">the Future</span>
            </motion.h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { img: phdImage, label: 'PhD & Advanced Research', sub: 'Doctoral-level scholarly work' },
              { img: iotImage, label: 'IoT & Embedded Systems', sub: 'Hardware + Software integration' },
              { img: techImage, label: 'Multi-Domain Expertise', sub: 'Cutting-edge technology stacks' },
            ].map(({ img, label, sub }, i) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, scale: 0.92 }}
                animate={galleryInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: i * 0.15, duration: 0.6 }}
                className="relative rounded-3xl overflow-hidden shadow-card-hover group"
              >
                <img
                  src={img}
                  alt={label}
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-royal-navy/80 via-royal-navy/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="font-playfair font-bold text-white text-xl mb-1">{label}</p>
                  <p className="font-inter text-white/70 text-sm">{sub}</p>
                </div>
                <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-royal-gold/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="section-pad bg-parchment" style={{ backgroundColor: '#FAFAF5' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div ref={testimonialsRef} className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={testimonialsInView ? { opacity: 1, y: 0 } : {}}
              className="inline-flex items-center gap-2 bg-royal-navy/15 border border-royal-navy/25 text-royal-navy text-sm font-bold px-5 py-2.5 rounded-full mb-6"
            >
              Student Success Stories
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={testimonialsInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.1 }}
              className="font-playfair text-4xl md:text-5xl font-bold text-royal-navy mb-4"
            >
              Loved by{' '}
              <span style={{ color: '#A07830' }}>Thousands</span>
            </motion.h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 30 }}
                animate={testimonialsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl p-6 relative group hover:-translate-y-1 transition-transform duration-300 shadow-card border border-royal-navy/10"
              >
                {/* Stars */}
                <div className="flex gap-1 mb-4">
                  {[...Array(t.rating)].map((_, j) => (
                    <svg key={j} className="w-4 h-4 text-royal-gold" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"/>
                    </svg>
                  ))}
                </div>

                {/* Quote */}
                <p className="font-inter text-sm text-slate-royal/80 leading-relaxed mb-6 italic">
                  "{t.text}"
                </p>

                {/* Author */}
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full ${t.color} flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}>
                    {t.avatar}
                  </div>
                  <div>
                    <p className="font-inter font-semibold text-royal-navy text-sm">{t.name}</p>
                    <p className="font-inter text-xs text-slate-royal/60">{t.course}</p>
                  </div>
                </div>

                {/* Decorative quote mark */}
                <div className="absolute top-4 right-5 text-4xl text-royal-gold/20 font-playfair font-bold">"</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
